import { randomUuid } from '@Core/lib/tasks/utils/text';
import type { SdkResource } from '@Pages/pageRuntime/sdk/types';

/** A stable text form of submitted inputs: object keys are sorted, array order is kept. */
export function inputFingerprint(inputs: Record<string, unknown>): string {
  return JSON.stringify(inputs, (_key, value) => {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return value;
    }

    return Object.fromEntries(
      Object.entries(value).toSorted(([left], [right]) => left.localeCompare(right))
    );
  });
}

/** The key of the last submit and the fingerprint of the inputs that it was made for. */
export type ManagedIdempotencyKey = { fingerprint: string; key: string };

/**
 * The key field of a booking resource. `idempotent: true` writes a new key into this field, so a
 * resource without the mapping cannot use the option.
 */
export function bookingIdempotencyFieldKey(resource: Pick<SdkResource, 'capabilities'>): string {
  const key = resource.capabilities?.booking?.idempotencyField?.key;

  if (typeof key !== 'string' || key === '') {
    throw new Error(
      '`idempotent: true` needs a booking resource with an idempotency field. Map the idempotency ' +
        'field in the booking capability of the resource, or remove `idempotent`.'
    );
  }

  return key;
}

/**
 * Writes the idempotency key into the payload. A retry with the same payload keeps the key of the
 * last submit, so the backend returns the booking that the first attempt made. A changed payload
 * gets a new key.
 */
export function withIdempotencyKey(
  payload: Record<string, unknown>,
  fieldKey: string,
  previous: ManagedIdempotencyKey | null
): { payload: Record<string, unknown>; managed: ManagedIdempotencyKey } {
  const inputs: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(payload)) {
    if (key !== fieldKey) {
      inputs[key] = value;
    }
  }

  const fingerprint = inputFingerprint(inputs);
  const managed =
    previous !== null && previous.fingerprint === fingerprint
      ? previous
      : { fingerprint, key: randomUuid() };

  return { payload: { ...inputs, [fieldKey]: managed.key }, managed };
}
